/**
 * Intranet Osasco Plaza — src/modules/ronda.jsx
 * Telas do módulo Gestão de Ronda — Fase Ronda-1 (fundação + execução com QR
 * + GPS, ver DOC/ADR/0013) + Fase Ronda-2 (foto de evidência OPCIONAL por
 * checkpoint, R2, ver DOC/ADR/0014) + Fase Ronda-3 (ocorrências, pânico,
 * mapa em tempo real do Painel, auditoria, relatório de conformidade,
 * dispositivo autorizado por rondante — ver DOC/ADR/0015) + Fase Ronda-4
 * (alertas automáticos com reconhecimento formal, bloqueio de ronda fora de
 * ordem, trilha histórica de posição no mapa, geocerca — formaliza o raio de
 * tolerância que já existia desde a Fase 1 —, leitura opcional por tag NFC
 * como alternativa ao QR — ver DOC/ADR/0016). Ainda sem offline/push — fora
 * de escopo. Ver DOC/FASE_RONDA_D1_PLANO.md (referência VicStaffO).
 *
 * Duas visões:
 *   Admin/Gestor — Pontos (com QR e tag NFC opcional), Rotas, Programações,
 *                   Atribuições, Painel do dia (mapa ao vivo + trajeto
 *                   percorrido + alertas do dia + miniatura da foto no
 *                   detalhe da execução), Ocorrências, Alertas (reconhecer),
 *                   Auditoria, Relatório, Dispositivos.
 *   Rondante     — "Minhas Rondas": lista do dia → iniciar → tela de
 *                   execução (ler QR com a câmera OU tag NFC + GPS a cada
 *                   ponto, com opção de anexar foto, registrar ocorrência e
 *                   acionar pânico).
 */

import { Blocked, Icon, fmtDate, hasPerm, TimeSelect } from './shared.jsx';
import { qrGenerateSVG, baixarQRPng, imprimirQR } from './qr.js';
const { useState, useEffect, useRef } = React;

const DIAS_SEMANA = [[0,'Dom'],[1,'Seg'],[2,'Ter'],[3,'Qua'],[4,'Qui'],[5,'Sex'],[6,'Sáb']];

const TIPOS_OCORRENCIA = [['seguranca','Segurança'],['manutencao','Manutenção'],['limpeza','Limpeza'],['outro','Outro']];
const GRAVIDADES = [['baixa','Baixa'],['media','Média'],['alta','Alta']];

function statusExecucaoInfo(status) {
  switch (status) {
    case 'em_andamento':        return { cls:'warn',  label:'Em andamento' };
    case 'concluida':           return { cls:'ok',    label:'Concluída' };
    case 'concluida_com_desvio':return { cls:'warn',  label:'Concluída c/ desvio' };
    case 'cancelada':           return { cls:'muted', label:'Cancelada' };
    default:                    return { cls:'muted', label: status || '—' };
  }
}
function statusCheckpointInfo(status) {
  switch (status) {
    case 'ok':          return { cls:'ok',   label:'OK' };
    case 'fora_de_raio': return { cls:'warn',label:'Fora do raio' };
    case 'invalido':     return { cls:'err', label:'Inválido' };
    default:             return { cls:'muted', label: status || '—' };
  }
}
function gravidadeInfo(g) {
  switch (g) {
    case 'alta':  return { cls:'err',  label:'Alta' };
    case 'media': return { cls:'warn', label:'Média' };
    default:      return { cls:'muted',label:'Baixa' };
  }
}
function tipoOcorrenciaLabel(tipo) {
  if (tipo === 'panico') return '🚨 Pânico';
  return (TIPOS_OCORRENCIA.find(([v]) => v === tipo) || [null, tipo])[1] || tipo;
}

/* Busca a foto do checkpoint como blob autenticado e mostra como <img> —
   mesmo padrão de SegFoto em seg.jsx (Fase Ronda-2, ver DOC/ADR/0014). */
function RondaFoto({ checkpointId, className, onClick, alt, style }) {
  const [url, setUrl] = useState(null);
  const [erro, setErro] = useState(false);
  useEffect(() => {
    let vivo = true, criado = null;
    API.ronda.fotoCheckpointUrl(checkpointId)
      .then(u => { if (vivo) { criado = u; setUrl(u); } else URL.revokeObjectURL(u); })
      .catch(() => { if (vivo) setErro(true); });
    return () => { vivo = false; if (criado) URL.revokeObjectURL(criado); };
  }, [checkpointId]);
  if (erro) return null;
  if (!url) return <div className={className} style={{ ...style, background:'var(--surface-2)', display:'flex', alignItems:'center', justifyContent:'center' }}><Icon name="camera" size={16} style={{ color:'var(--ink-4)' }}/></div>;
  return <img src={url} alt={alt||'Foto do checkpoint'} className={className} style={style} onClick={onClick}/>;
}

/* Identificador do dispositivo (Fase Ronda-3, ver DOC/ADR/0015) — gerado
   uma vez neste navegador e persistido em localStorage, NÃO é fingerprint
   de hardware, nenhuma lib nova. Enviado junto de iniciar execução; o
   worker decide se é o primeiro (autoriza sozinho) ou um diferente (só
   avisa, nunca bloqueia — ver `registrarDispositivo` no worker). Se
   localStorage estiver bloqueado, devolve null e a execução segue normal
   sem vínculo de dispositivo nenhum. */
const CHAVE_DEVICE_ID = 'vs_ronda_device_id';
function obterDeviceId() {
  try {
    let id = localStorage.getItem(CHAVE_DEVICE_ID);
    if (!id) { id = crypto.randomUUID(); localStorage.setItem(CHAVE_DEVICE_ID, id); }
    return id;
  } catch { return null; }
}
/* Trecho curto do user-agent — só ajuda o admin a reconhecer visualmente
   qual aparelho é qual na aba Dispositivos, não é usado pra nada técnico. */
function nomeDispositivoAtual() {
  const ua = navigator.userAgent || '';
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/Android/.test(ua)) return 'Android';
  if (/iPad/.test(ua)) return 'iPad';
  return 'Navegador desktop';
}

/** Localização atual, sem nunca travar o fluxo — GPS indisponível vira
 *  {lat:null,lng:null} e o worker simplesmente não valida a distância. */
function obterPosicao() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ lat:null, lng:null });
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat:pos.coords.latitude, lng:pos.coords.longitude }),
      () => resolve({ lat:null, lng:null }),
      { enableHighAccuracy:true, timeout:8000 }
    );
  });
}

/* ── Carregamento preguiçoso do jsQR — só quando a câmera é aberta.
   Domínio liberado no script-src da CSP desde a Fase Ronda-1 (DOC/ADR/0013),
   única exceção de terceiro que este projeto já teve. */
let _jsQRPromise = null;
function carregarJsQR() {
  if (window.jsQR) return Promise.resolve(window.jsQR);
  if (_jsQRPromise) return _jsQRPromise;
  _jsQRPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js';
    s.onload = () => resolve(window.jsQR);
    s.onerror = () => reject(new Error('Não foi possível carregar o leitor de QR.'));
    document.head.appendChild(s);
  });
  return _jsQRPromise;
}

/* ── NFC (Web NFC API) — Fase Ronda-4, Etapa 5. É uma forma ALTERNATIVA de
   identificar um ponto, nunca a única: só existe no Chrome for Android, com
   HTTPS e permissão concedida a cada leitura — sem suporte em iOS/desktop,
   nem instalação de lib (é uma API nativa do navegador, sem CDN). O app
   sempre continua funcionando por QR/código pra quem não tem o recurso —
   mesmo raciocínio de "melhor esforço" já usado pra foto/posição. */
function nfcSuportado() { return typeof window !== 'undefined' && 'NDEFReader' in window; }
function lerTagNFC() {
  return new Promise(async (resolve, reject) => {
    if (!nfcSuportado()) {
      reject(new Error('Leitura NFC não é suportada neste navegador/dispositivo (funciona só no Chrome for Android, em HTTPS).'));
      return;
    }
    try {
      const reader = new window.NDEFReader();
      await reader.scan();
      reader.onreading = (event) => { reader.onreading = null; reader.onreadingerror = null; resolve(event.serialNumber); };
      reader.onreadingerror = () => { reader.onreading = null; reader.onreadingerror = null; reject(new Error('Não foi possível ler a tag NFC. Tente novamente.')); };
    } catch {
      reject(new Error('Leitura NFC cancelada ou sem permissão.'));
    }
  });
}

/* ── Carregamento preguiçoso do Leaflet — só quando o Painel do Supervisor
   monta. Domínio liberado no script-src/style-src da CSP desde a Fase
   Ronda-3 (DOC/ADR/0015), segunda exceção de terceiro deste projeto. CSS +
   JS, nessa ordem (o JS lê estilo computado dos ícones no boot). */
let _leafletPromise = null;
function carregarLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (_leafletPromise) return _leafletPromise;
  _leafletPromise = new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css';
    document.head.appendChild(link);

    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
    s.onload = () => resolve(window.L);
    s.onerror = () => reject(new Error('Não foi possível carregar o mapa.'));
    document.head.appendChild(s);
  });
  return _leafletPromise;
}

/* ── Mapa em tempo real do Painel do Supervisor — pontos cadastrados +
   posição ao vivo de cada execução em andamento. Reaproveita a MESMA
   instância do Leaflet entre re-renders (só os marcadores são recriados) —
   preserva o zoom/pan que o supervisor tiver ajustado manualmente. */
function RondaMapa({ pontos, execucoesAoVivo }) {
  const divRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [erro, setErro] = useState('');

  useEffect(() => {
    let vivo = true;
    carregarLeaflet().then((L) => {
      if (!vivo || !divRef.current || mapRef.current) return;
      const map = L.map(divRef.current).setView([-23.5320, -46.7920], 16);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
        maxZoom: 19,
      }).addTo(map);
      mapRef.current = map;
      atualizarMarcadores();
    }).catch((e) => { if (vivo) setErro(e.message || 'Não foi possível carregar o mapa.'); });
    return () => { vivo = false; };
  }, []);

  const atualizarMarcadores = () => {
    const L = window.L, map = mapRef.current;
    if (!L || !map) return;
    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];

    const bounds = [];
    (pontos || []).filter(p => p.latitude != null && p.longitude != null).forEach(p => {
      const m = L.circleMarker([p.latitude, p.longitude], { radius:6, color:'#64748b', fillColor:'#94a3b8', fillOpacity:0.8 })
        .bindPopup(`<b>${p.nome}</b><br/>#${p.codigo}`).addTo(map);
      markersRef.current.push(m);
      bounds.push([p.latitude, p.longitude]);
    });

    (execucoesAoVivo || []).filter(ex => ex.latAtual != null && ex.lngAtual != null).forEach(ex => {
      const cor = ex.panicoAcionado ? '#dc2626' : '#1d4ed8';
      const m = L.circleMarker([ex.latAtual, ex.lngAtual], { radius:9, color:cor, fillColor:cor, fillOpacity:0.9, weight:2 })
        .bindPopup(`<b>${ex.usuarioNome || 'Rondante'}</b><br/>${ex.rotaNome || ''}${ex.panicoAcionado ? '<br/><b style="color:#dc2626">🚨 Pânico acionado</b>' : ''}`)
        .addTo(map);
      markersRef.current.push(m);
      bounds.push([ex.latAtual, ex.lngAtual]);
    });

    if (bounds.length && !mapRef.current._rondaFitDone) {
      map.fitBounds(bounds, { padding:[24,24] });
      mapRef.current._rondaFitDone = true;
    }
  };

  useEffect(() => { if (mapRef.current) atualizarMarcadores(); }, [pontos, execucoesAoVivo]);
  useEffect(() => () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } }, []);

  if (erro) return <div className="card" style={{padding:16,color:'var(--ink-3)',fontSize:12}}>{erro}</div>;
  return <div ref={divRef} className="ronda-map-box"/>;
}

/* ── Trilha histórica de posição (Fase Ronda-4, Etapa 3, ver DOC/ADR/0016)
   — mapa de UMA execução só, sem polling (não é ao vivo): pontos cadastrados
   como backdrop + polyline ligando as posições registradas + marcador de
   início (verde) e última posição (vermelho). Mapa novo a cada abertura,
   não reaproveita instância (diferente de RondaMapa, que é o painel ao vivo
   com refresh contínuo). */
function RondaTrajetoMapa({ pontos, trajeto }) {
  const divRef = useRef(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    let vivo = true, mapa = null;
    carregarLeaflet().then((L) => {
      if (!vivo || !divRef.current) return;
      mapa = L.map(divRef.current).setView([-23.5320, -46.7920], 16);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap', maxZoom: 19 }).addTo(mapa);

      const bounds = [];
      (pontos || []).filter(p => p.latitude != null && p.longitude != null).forEach(p => {
        L.circleMarker([p.latitude, p.longitude], { radius:6, color:'#64748b', fillColor:'#94a3b8', fillOpacity:0.8 })
          .bindPopup(`<b>${p.nome}</b>`).addTo(mapa);
        bounds.push([p.latitude, p.longitude]);
      });

      const linha = (trajeto || []).map(p => [p.lat, p.lng]);
      if (linha.length) {
        L.polyline(linha, { color:'#1d4ed8', weight:3 }).addTo(mapa);
        L.circleMarker(linha[0], { radius:8, color:'#16a34a', fillColor:'#16a34a', fillOpacity:1 }).bindPopup('Início').addTo(mapa);
        L.circleMarker(linha[linha.length-1], { radius:8, color:'#dc2626', fillColor:'#dc2626', fillOpacity:1 }).bindPopup('Última posição').addTo(mapa);
        bounds.push(...linha);
      }
      if (bounds.length) mapa.fitBounds(bounds, { padding:[24,24] });
    }).catch((e) => { if (vivo) setErro(e.message || 'Não foi possível carregar o mapa.'); });
    return () => { vivo = false; if (mapa) mapa.remove(); };
  }, []);

  if (erro) return <div className="card" style={{padding:16,color:'var(--ink-3)',fontSize:12}}>{erro}</div>;
  if (!trajeto || trajeto.length === 0) return <div className="card" style={{padding:16,color:'var(--ink-3)',fontSize:12,textAlign:'center'}}>Nenhuma posição registrada nesta execução.</div>;
  return <div ref={divRef} className="ronda-map-box"/>;
}

/* ── QR do Ponto — o conteúdo é o próprio código do ponto (não uma URL: quem
   lê é a própria tela de execução via câmera, não o navegador). */
function RondaQRModal({ ponto, onClose }) {
  const svg = qrGenerateSVG(ponto.codigo, { size:220, margin:3, level:'M' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const baixar = async () => {
    setBusy(true); setErr('');
    try { await baixarQRPng(svg, `ronda-${ponto.codigo}.png`, 512); }
    catch (e) { setErr(e.message || 'Não foi possível gerar o PNG.'); }
    setBusy(false);
  };
  const imprimir = () => imprimirQR(svg, `${ponto.codigo} — ${ponto.nome}`);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e=>e.stopPropagation()} style={{maxWidth:340,textAlign:'center'}}>
        <button className="close-x" onClick={onClose}><Icon name="x" size={16}/></button>
        <h3>QR do Ponto de Ronda</h3>
        <p style={{fontSize:13,color:'var(--ink-3)',marginBottom:16}}>#{ponto.codigo} — {ponto.nome}</p>
        <div style={{display:'flex',justifyContent:'center',marginBottom:16}} dangerouslySetInnerHTML={{__html: svg}}/>
        <p style={{fontSize:11,color:'var(--ink-4)',marginBottom:16}}>
          Cole este QR no local físico do ponto. Na execução da ronda, o rondante aponta a câmera para ele.
        </p>
        {err && <p style={{color:'var(--err)',fontSize:12,marginBottom:12}}>{err}</p>}
        <div style={{display:'flex',gap:10,justifyContent:'center'}}>
          <button className="btn btn-ghost" onClick={imprimir}><Icon name="printer" size={14}/> Imprimir</button>
          <button className="btn btn-primary" onClick={baixar} disabled={busy}><Icon name="download" size={14}/> {busy?'Gerando...':'Baixar PNG'}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Leitor de QR por câmera ao vivo (getUserMedia + jsQR) ── */
function LeitorQR({ onLido, onCancelar }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const jsQR = await carregarJsQR();
        const stream = await navigator.mediaDevices.getUserMedia({ video:{ facingMode:'environment' } });
        if (!vivo) { stream.getTracks().forEach(t=>t.stop()); return; }
        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        if (!vivo) return;
        setCarregando(false);
        const tick = () => {
          if (!vivo) return;
          const video = videoRef.current, canvas = canvasRef.current;
          if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
            canvas.width = video.videoWidth; canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const code = jsQR(imgData.data, imgData.width, imgData.height);
            if (code && code.data) { onLido(code.data); return; }
          }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch (e) {
        if (vivo) setErro(e.message || 'Não foi possível acessar a câmera.');
      }
    })();
    return () => {
      vivo = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t=>t.stop());
    };
  }, []);

  return (
    <div className="modal-overlay" onClick={onCancelar}>
      <div className="modal-card" onClick={e=>e.stopPropagation()} style={{maxWidth:400,textAlign:'center'}}>
        <button className="close-x" onClick={onCancelar}><Icon name="x" size={16}/></button>
        <h3>Ler QR do Ponto</h3>
        {erro ? (
          <p style={{color:'var(--err)',fontSize:13,marginTop:12}}>{erro}</p>
        ) : (
          <>
            <div className="ronda-scan-box">
              <video ref={videoRef} playsInline muted style={{width:'100%',borderRadius:8,background:'#000'}}/>
              <canvas ref={canvasRef} style={{display:'none'}}/>
            </div>
            {carregando && <p style={{fontSize:12,color:'var(--ink-3)',marginTop:8}}>Abrindo câmera…</p>}
          </>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   PONTOS
   ══════════════════════════════════════════════════════════════ */
export function RondaPontosScreen({ user }) {
  const [pontos, setPontos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const [modal, setModal] = useState(null);
  const [qrPonto, setQrPonto] = useState(null);
  const [form, setForm] = useState({ codigo:'', nome:'', descricao:'', latitude:'', longitude:'', raioMetros:50, nfcUid:'' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [lendoNfcAdmin, setLendoNfcAdmin] = useState(false);

  const canEdit = hasPerm(user, 'ronda', 'edit');

  const refresh = async () => {
    setCarregando(true); setErroCarga('');
    try { const r = await API.ronda.pontos(); setPontos(r.pontos || []); }
    catch (e) { setErroCarga(e.message || 'Não foi possível carregar os pontos.'); }
    setCarregando(false);
  };
  useEffect(() => { refresh(); }, []);

  const openNew = () => { setForm({ codigo:'', nome:'', descricao:'', latitude:'', longitude:'', raioMetros:50, nfcUid:'' }); setErr(''); setModal('new'); };
  const openEdit = (p) => { setForm({ codigo:p.codigo, nome:p.nome, descricao:p.descricao||'', latitude:p.latitude??'', longitude:p.longitude??'', raioMetros:p.raioMetros||50, nfcUid:p.nfcUid||'' }); setErr(''); setModal(p); };

  const lerNfcParaForm = async () => {
    setLendoNfcAdmin(true); setErr('');
    try { const uid = await lerTagNFC(); setForm(f=>({...f, nfcUid: uid})); }
    catch (e) { setErr(e.message || 'Não foi possível ler a tag.'); }
    setLendoNfcAdmin(false);
  };

  const usarLocalizacaoAtual = () => {
    if (!navigator.geolocation) { setErr('Geolocalização não disponível neste navegador.'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm(f=>({...f, latitude:pos.coords.latitude.toFixed(6), longitude:pos.coords.longitude.toFixed(6)})),
      () => setErr('Não foi possível obter a localização atual.')
    );
  };

  const save = async () => {
    if (!form.codigo.trim() || !form.nome.trim()) { setErr('Preencha código e nome.'); return; }
    setBusy(true); setErr('');
    try {
      const payload = { ...form, latitude: form.latitude===''?null:Number(form.latitude), longitude: form.longitude===''?null:Number(form.longitude) };
      if (modal === 'new') await API.ronda.criarPonto(payload);
      else await API.ronda.editarPonto(modal.id, payload);
      await refresh(); setModal(null);
    } catch (e) { setErr(e.message || 'Não foi possível salvar o ponto.'); }
    setBusy(false);
  };

  const desativar = async (id) => {
    if (!confirm('Desativar este ponto? Ele some das rotas novas, mas o histórico de checkpoints é preservado.')) return;
    try { await API.ronda.desativarPonto(id); await refresh(); }
    catch (e) { alert(e.message || 'Não foi possível desativar o ponto.'); }
  };

  if (!hasPerm(user, 'ronda', 'view')) return <Blocked/>;

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title"><h2>Pontos de Ronda</h2><p>Locais físicos de checagem — cada um gera um QR para colar no local</p></div>
        {canEdit && <button className="btn btn-primary" onClick={openNew}><Icon name="plus" size={14}/> Novo Ponto</button>}
      </div>

      {carregando && <div className="card" style={{padding:20,color:'var(--ink-3)',fontSize:13,marginBottom:16}}>Carregando pontos…</div>}
      {erroCarga && (
        <div className="card" style={{padding:16,marginBottom:16,background:'var(--err-bg)',border:'1px solid #f5c2c2'}}>
          <div style={{color:'var(--err)',fontSize:13,fontWeight:600,marginBottom:6}}>{erroCarga}</div>
          <button className="btn btn-ghost" onClick={refresh}>Tentar de novo</button>
        </div>
      )}

      {!carregando && !erroCarga && (pontos.length===0 ? (
        <div className="card" style={{textAlign:'center',padding:40,color:'var(--ink-3)'}}>
          <Icon name="map-pin" size={32} style={{margin:'0 auto 12px',display:'block'}}/>
          <p>Nenhum ponto cadastrado.</p>
          {canEdit && <button className="btn btn-primary" style={{marginTop:16}} onClick={openNew}><Icon name="plus" size={14}/> Cadastrar primeiro ponto</button>}
        </div>
      ) : (
        <div className="ronda-grid">
          {pontos.map(p => (
            <div key={p.id} className="card">
              <div style={{fontSize:11,color:'var(--ink-4)',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.06em'}}>#{p.codigo}</div>
              <div style={{fontWeight:600,fontSize:15,marginTop:2}}>{p.nome}</div>
              {p.descricao && <div style={{fontSize:12,color:'var(--ink-3)',marginTop:4}}>{p.descricao}</div>}
              <div style={{display:'flex',gap:10,marginTop:12,flexWrap:'wrap',fontSize:11,color:'var(--ink-3)'}}>
                {(p.latitude!=null && p.longitude!=null)
                  ? <span>{Number(p.latitude).toFixed(5)}, {Number(p.longitude).toFixed(5)}</span>
                  : <span>Sem coordenada cadastrada</span>}
                <span>raio {p.raioMetros}m</span>
                {p.nfcUid && <span><Icon name="nfc" size={11} style={{verticalAlign:'-1px'}}/> tag NFC</span>}
              </div>
              <div style={{display:'flex',gap:8,marginTop:14}}>
                <button className="btn btn-ghost btn-sm" onClick={()=>setQrPonto(p)}><Icon name="qr-code" size={13}/> QR</button>
                {canEdit && <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(p)}><Icon name="edit" size={13}/></button>}
                {canEdit && <button className="btn btn-danger btn-sm" onClick={()=>desativar(p.id)}><Icon name="trash" size={13}/></button>}
              </div>
            </div>
          ))}
        </div>
      ))}

      {modal && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal-card" onClick={e=>e.stopPropagation()} style={{maxWidth:480}}>
            <button className="close-x" onClick={()=>setModal(null)}><Icon name="x" size={16}/></button>
            <h3>{modal==='new' ? 'Novo Ponto' : 'Editar Ponto'}</h3>
            <p style={{fontSize:13,color:'var(--ink-3)',marginBottom:20}}>Cadastre o local físico de checagem</p>
            <div className="seg-field-row">
              <div className="field"><label>Código <span className="req">*</span></label><input value={form.codigo} onChange={e=>setForm(f=>({...f,codigo:e.target.value}))} placeholder="PONTO-001"/></div>
              <div className="field"><label>Nome <span className="req">*</span></label><input value={form.nome} onChange={e=>setForm(f=>({...f,nome:e.target.value}))} placeholder="Ex: Portaria Social"/></div>
            </div>
            <div className="field"><label>Descrição</label><input value={form.descricao} onChange={e=>setForm(f=>({...f,descricao:e.target.value}))} placeholder="Ex: Próximo à guarita"/></div>
            <div className="seg-field-row">
              <div className="field"><label>Latitude</label><input value={form.latitude} onChange={e=>setForm(f=>({...f,latitude:e.target.value}))} placeholder="-23.532000"/></div>
              <div className="field"><label>Longitude</label><input value={form.longitude} onChange={e=>setForm(f=>({...f,longitude:e.target.value}))} placeholder="-46.792000"/></div>
            </div>
            <button className="btn btn-ghost btn-sm" style={{marginBottom:14}} onClick={usarLocalizacaoAtual}><Icon name="map-pin" size={13}/> Usar localização atual</button>
            <div className="field"><label>Raio de tolerância (metros)</label><input type="number" min="10" value={form.raioMetros} onChange={e=>setForm(f=>({...f,raioMetros:e.target.value}))}/></div>
            <div className="field">
              <label>Tag NFC (opcional)</label>
              <div style={{display:'flex',gap:8}}>
                <input value={form.nfcUid} onChange={e=>setForm(f=>({...f,nfcUid:e.target.value}))} placeholder="Aproxime a tag ou cole o ID"/>
                <button type="button" className="btn btn-ghost btn-sm" onClick={lerNfcParaForm} disabled={lendoNfcAdmin}>
                  <Icon name="nfc" size={13}/> {lendoNfcAdmin?'Lendo...':'Ler tag'}
                </button>
              </div>
              <p style={{fontSize:11,color:'var(--ink-4)',marginTop:4}}>Alternativa ao QR na hora do checkpoint. Só funciona no Chrome for Android — nos outros aparelhos o rondante continua lendo o QR normalmente.</p>
            </div>
            {err && <p style={{color:'var(--err)',fontSize:12,marginBottom:12}}>{err}</p>}
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Salvando...':'Salvar Ponto'}</button>
            </div>
          </div>
        </div>
      )}

      {qrPonto && <RondaQRModal ponto={qrPonto} onClose={()=>setQrPonto(null)}/>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   ROTAS
   ══════════════════════════════════════════════════════════════ */
export function RondaRotasScreen({ user }) {
  const [rotas, setRotas] = useState([]);
  const [pontos, setPontos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ nome:'', descricao:'', duracaoEstimadaMin:'' });
  const [seqPontos, setSeqPontos] = useState([]);
  const [addPontoId, setAddPontoId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const canEdit = hasPerm(user, 'ronda', 'edit');

  const refresh = async () => {
    setCarregando(true); setErroCarga('');
    try {
      const [r, p] = await Promise.all([API.ronda.rotas(), API.ronda.pontos()]);
      setRotas(r.rotas || []); setPontos(p.pontos || []);
    } catch (e) { setErroCarga(e.message || 'Não foi possível carregar as rotas.'); }
    setCarregando(false);
  };
  useEffect(() => { refresh(); }, []);

  const openNew = () => { setForm({ nome:'', descricao:'', duracaoEstimadaMin:'' }); setSeqPontos([]); setAddPontoId(''); setErr(''); setModal('new'); };
  const openEdit = (r) => {
    setForm({ nome:r.nome, descricao:r.descricao||'', duracaoEstimadaMin:r.duracaoEstimadaMin||'' });
    setSeqPontos((r.pontos||[]).map(p=>({ pontoId:p.pontoId, pontoNome:p.pontoNome, toleranciaAntesMin:p.toleranciaAntesMin, toleranciaDepoisMin:p.toleranciaDepoisMin, obrigatorio:p.obrigatorio, instrucoes:p.instrucoes||'' })));
    setAddPontoId(''); setErr(''); setModal(r);
  };

  const adicionarPonto = () => {
    if (!addPontoId) return;
    const p = pontos.find(x=>x.id===addPontoId);
    if (!p || seqPontos.some(x=>x.pontoId===p.id)) return;
    setSeqPontos(l => [...l, { pontoId:p.id, pontoNome:p.nome, toleranciaAntesMin:5, toleranciaDepoisMin:10, obrigatorio:true, instrucoes:'' }]);
    setAddPontoId('');
  };
  const moverPonto = (idx, dir) => {
    setSeqPontos(l => {
      const novo = [...l]; const alvo = idx + dir;
      if (alvo < 0 || alvo >= novo.length) return l;
      [novo[idx], novo[alvo]] = [novo[alvo], novo[idx]];
      return novo;
    });
  };
  const removerPonto = (idx) => setSeqPontos(l => l.filter((_,i)=>i!==idx));

  const save = async () => {
    if (!form.nome.trim()) { setErr('Informe o nome da rota.'); return; }
    if (!seqPontos.length) { setErr('Adicione ao menos um ponto à rota.'); return; }
    setBusy(true); setErr('');
    try {
      const payload = { ...form, pontos: seqPontos.map((p,i)=>({ pontoId:p.pontoId, sequencia:i+1, toleranciaAntesMin:p.toleranciaAntesMin, toleranciaDepoisMin:p.toleranciaDepoisMin, obrigatorio:p.obrigatorio, instrucoes:p.instrucoes })) };
      if (modal==='new') await API.ronda.criarRota(payload);
      else await API.ronda.editarRota(modal.id, payload);
      await refresh(); setModal(null);
    } catch (e) { setErr(e.message || 'Não foi possível salvar a rota.'); }
    setBusy(false);
  };

  const desativar = async (id) => {
    if (!confirm('Desativar esta rota? Programações que a usam deixam de gerar novas rondas.')) return;
    try { await API.ronda.desativarRota(id); await refresh(); }
    catch (e) { alert(e.message || 'Não foi possível desativar a rota.'); }
  };

  if (!hasPerm(user, 'ronda', 'view')) return <Blocked/>;

  const pontosDisponiveis = pontos.filter(p => !seqPontos.some(s=>s.pontoId===p.id));

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title"><h2>Rotas de Ronda</h2><p>Sequência de pontos que o rondante percorre</p></div>
        {canEdit && <button className="btn btn-primary" onClick={openNew}><Icon name="plus" size={14}/> Nova Rota</button>}
      </div>

      {carregando && <div className="card" style={{padding:20,color:'var(--ink-3)',fontSize:13,marginBottom:16}}>Carregando rotas…</div>}
      {erroCarga && (
        <div className="card" style={{padding:16,marginBottom:16,background:'var(--err-bg)',border:'1px solid #f5c2c2'}}>
          <div style={{color:'var(--err)',fontSize:13,fontWeight:600,marginBottom:6}}>{erroCarga}</div>
          <button className="btn btn-ghost" onClick={refresh}>Tentar de novo</button>
        </div>
      )}

      {!carregando && !erroCarga && (rotas.length===0 ? (
        <div className="card" style={{textAlign:'center',padding:40,color:'var(--ink-3)'}}>
          <Icon name="list" size={32} style={{margin:'0 auto 12px',display:'block'}}/>
          <p>Nenhuma rota cadastrada.</p>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {rotas.map(r => (
            <div key={r.id} className="card">
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12}}>
                <div>
                  <div style={{fontWeight:600,fontSize:15}}>{r.nome}</div>
                  {r.descricao && <div style={{fontSize:12,color:'var(--ink-3)',marginTop:2}}>{r.descricao}</div>}
                  <div style={{fontSize:11,color:'var(--ink-4)',marginTop:6}}>{r.pontos.length} ponto(s){r.duracaoEstimadaMin?` · ~${r.duracaoEstimadaMin} min`:''}</div>
                </div>
                {canEdit && <div style={{display:'flex',gap:8}}>
                  <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(r)}><Icon name="edit" size={13}/></button>
                  <button className="btn btn-danger btn-sm" onClick={()=>desativar(r.id)}><Icon name="trash" size={13}/></button>
                </div>}
              </div>
              {r.pontos.length>0 && (
                <div style={{marginTop:10,paddingTop:10,borderTop:'1px solid var(--line)',fontSize:12,color:'var(--ink-3)'}}>
                  {r.pontos.map((p,i)=>`${i+1}. ${p.pontoNome}${p.obrigatorio?'':' (opcional)'}`).join('  →  ')}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}

      {modal && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal-card" onClick={e=>e.stopPropagation()} style={{maxWidth:600}}>
            <button className="close-x" onClick={()=>setModal(null)}><Icon name="x" size={16}/></button>
            <h3>{modal==='new' ? 'Nova Rota' : 'Editar Rota'}</h3>
            <div className="seg-field-row">
              <div className="field"><label>Nome <span className="req">*</span></label><input value={form.nome} onChange={e=>setForm(f=>({...f,nome:e.target.value}))} placeholder="Ex: Rota Noturna — Piso 1"/></div>
              <div className="field"><label>Duração estimada (min)</label><input type="number" min="1" value={form.duracaoEstimadaMin} onChange={e=>setForm(f=>({...f,duracaoEstimadaMin:e.target.value}))}/></div>
            </div>
            <div className="field"><label>Descrição</label><input value={form.descricao} onChange={e=>setForm(f=>({...f,descricao:e.target.value}))}/></div>

            <div style={{marginTop:16,marginBottom:8,fontWeight:600,fontSize:13}}>Sequência de pontos</div>
            <div style={{display:'flex',gap:8,marginBottom:12}}>
              <select style={{flex:1}} value={addPontoId} onChange={e=>setAddPontoId(e.target.value)}>
                <option value="">Selecione um ponto para adicionar...</option>
                {pontosDisponiveis.map(p=><option key={p.id} value={p.id}>#{p.codigo} — {p.nome}</option>)}
              </select>
              <button className="btn btn-ghost" onClick={adicionarPonto} disabled={!addPontoId}><Icon name="plus" size={14}/></button>
            </div>

            {seqPontos.length===0 ? (
              <p style={{fontSize:12,color:'var(--ink-3)',marginBottom:12}}>Nenhum ponto na sequência ainda.</p>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:16}}>
                {seqPontos.map((p,i)=>(
                  <div key={p.pontoId} style={{border:'1px solid var(--line)',borderRadius:8,padding:'10px 12px'}}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <span style={{fontWeight:700,fontSize:12,color:'var(--ink-4)'}}>{i+1}.</span>
                      <span style={{flex:1,fontSize:13,fontWeight:600}}>{p.pontoNome}</span>
                      <button className="btn btn-ghost btn-sm" onClick={()=>moverPonto(i,-1)} disabled={i===0}><Icon name="chevron-left" size={12}/></button>
                      <button className="btn btn-ghost btn-sm" onClick={()=>moverPonto(i,1)} disabled={i===seqPontos.length-1}><Icon name="chevron-right" size={12}/></button>
                      <button className="btn btn-danger btn-sm" onClick={()=>removerPonto(i)}><Icon name="x" size={12}/></button>
                    </div>
                    <div style={{display:'flex',gap:10,marginTop:8,alignItems:'center',flexWrap:'wrap',fontSize:12}}>
                      <label style={{display:'flex',alignItems:'center',gap:4}}>
                        <input type="checkbox" checked={p.obrigatorio} onChange={e=>setSeqPontos(l=>l.map((x,xi)=>xi===i?{...x,obrigatorio:e.target.checked}:x))}/> Obrigatório
                      </label>
                      <span>Tolerância antes: <input type="number" min="0" style={{width:56}} value={p.toleranciaAntesMin} onChange={e=>setSeqPontos(l=>l.map((x,xi)=>xi===i?{...x,toleranciaAntesMin:parseInt(e.target.value)||0}:x))}/> min</span>
                      <span>depois: <input type="number" min="0" style={{width:56}} value={p.toleranciaDepoisMin} onChange={e=>setSeqPontos(l=>l.map((x,xi)=>xi===i?{...x,toleranciaDepoisMin:parseInt(e.target.value)||0}:x))}/> min</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {err && <p style={{color:'var(--err)',fontSize:12,marginBottom:12}}>{err}</p>}
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Salvando...':'Salvar Rota'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   PROGRAMAÇÕES
   ══════════════════════════════════════════════════════════════ */
export function RondaProgramacoesScreen({ user }) {
  const [programacoes, setProgramacoes] = useState([]);
  const [rotas, setRotas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ nome:'', rotaId:'', diasSemana:[], horaInicio:'08:00', toleranciaInicioMin:15 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const canEdit = hasPerm(user, 'ronda', 'edit');

  const refresh = async () => {
    setCarregando(true); setErroCarga('');
    try {
      const [p, r] = await Promise.all([API.ronda.programacoes(), API.ronda.rotas()]);
      setProgramacoes(p.programacoes || []); setRotas(r.rotas || []);
    } catch (e) { setErroCarga(e.message || 'Não foi possível carregar as programações.'); }
    setCarregando(false);
  };
  useEffect(() => { refresh(); }, []);

  const openNew = () => { setForm({ nome:'', rotaId:rotas[0]?.id||'', diasSemana:[], horaInicio:'08:00', toleranciaInicioMin:15 }); setErr(''); setModal('new'); };
  const openEdit = (p) => { setForm({ nome:p.nome, rotaId:p.rotaId, diasSemana:p.diasSemana||[], horaInicio:p.horaInicio, toleranciaInicioMin:p.toleranciaInicioMin }); setErr(''); setModal(p); };

  const toggleDia = (d) => setForm(f => ({...f, diasSemana: f.diasSemana.includes(d) ? f.diasSemana.filter(x=>x!==d) : [...f.diasSemana, d].sort()}));

  const save = async () => {
    if (!form.nome.trim()) { setErr('Informe o nome da programação.'); return; }
    if (!form.rotaId) { setErr('Selecione a rota.'); return; }
    if (!form.diasSemana.length) { setErr('Selecione ao menos um dia da semana.'); return; }
    setBusy(true); setErr('');
    try {
      if (modal==='new') await API.ronda.criarProgramacao(form);
      else await API.ronda.editarProgramacao(modal.id, form);
      await refresh(); setModal(null);
    } catch (e) { setErr(e.message || 'Não foi possível salvar a programação.'); }
    setBusy(false);
  };

  const desativar = async (id) => {
    if (!confirm('Desativar esta programação?')) return;
    try { await API.ronda.desativarProgramacao(id); await refresh(); }
    catch (e) { alert(e.message || 'Não foi possível desativar.'); }
  };

  if (!hasPerm(user, 'ronda', 'view')) return <Blocked/>;

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title"><h2>Programação de Ronda</h2><p>Quando cada rota deve rodar</p></div>
        {canEdit && <button className="btn btn-primary" onClick={openNew}><Icon name="plus" size={14}/> Nova Programação</button>}
      </div>

      {carregando && <div className="card" style={{padding:20,color:'var(--ink-3)',fontSize:13,marginBottom:16}}>Carregando…</div>}
      {erroCarga && (
        <div className="card" style={{padding:16,marginBottom:16,background:'var(--err-bg)',border:'1px solid #f5c2c2'}}>
          <div style={{color:'var(--err)',fontSize:13,fontWeight:600,marginBottom:6}}>{erroCarga}</div>
          <button className="btn btn-ghost" onClick={refresh}>Tentar de novo</button>
        </div>
      )}

      {!carregando && !erroCarga && (programacoes.length===0 ? (
        <div className="card" style={{textAlign:'center',padding:40,color:'var(--ink-3)'}}>
          <Icon name="calendar" size={32} style={{margin:'0 auto 12px',display:'block'}}/>
          <p>Nenhuma programação cadastrada.</p>
          {rotas.length===0 && <p style={{fontSize:12,marginTop:8}}>Cadastre uma rota primeiro.</p>}
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {programacoes.map(p => (
            <div key={p.id} className="card" style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,flexWrap:'wrap'}}>
              <div>
                <div style={{fontWeight:600,fontSize:15}}>{p.nome}</div>
                <div style={{fontSize:12,color:'var(--ink-3)',marginTop:2}}>{p.rotaNome} · {p.horaInicio} · {(p.diasSemana||[]).map(d=>DIAS_SEMANA[d][1]).join(', ')}</div>
              </div>
              {canEdit && <div style={{display:'flex',gap:8}}>
                <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(p)}><Icon name="edit" size={13}/></button>
                <button className="btn btn-danger btn-sm" onClick={()=>desativar(p.id)}><Icon name="trash" size={13}/></button>
              </div>}
            </div>
          ))}
        </div>
      ))}

      {modal && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal-card" onClick={e=>e.stopPropagation()} style={{maxWidth:480}}>
            <button className="close-x" onClick={()=>setModal(null)}><Icon name="x" size={16}/></button>
            <h3>{modal==='new' ? 'Nova Programação' : 'Editar Programação'}</h3>
            <div className="field"><label>Nome <span className="req">*</span></label><input value={form.nome} onChange={e=>setForm(f=>({...f,nome:e.target.value}))} placeholder="Ex: Ronda Noturna"/></div>
            <div className="field"><label>Rota <span className="req">*</span></label>
              <select value={form.rotaId} onChange={e=>setForm(f=>({...f,rotaId:e.target.value}))}>
                <option value="">Selecione...</option>
                {rotas.map(r=><option key={r.id} value={r.id}>{r.nome}</option>)}
              </select>
            </div>
            <div className="field"><label>Dias da Semana <span className="req">*</span></label>
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                {DIAS_SEMANA.map(([d,l])=>(
                  <button key={d} type="button" onClick={()=>toggleDia(d)} style={{
                    padding:'6px 10px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit',
                    border:'1.5px solid ' + (form.diasSemana.includes(d) ? 'var(--navy)' : 'var(--line)'),
                    background: form.diasSemana.includes(d) ? 'var(--info-bg)' : 'var(--surface)',
                    color: form.diasSemana.includes(d) ? 'var(--navy)' : 'var(--ink-3)',
                  }}>{l}</button>
                ))}
              </div>
            </div>
            <div className="seg-field-row">
              <div className="field"><label>Horário de Início</label><TimeSelect value={form.horaInicio} onChange={v=>setForm(f=>({...f,horaInicio:v}))}/></div>
              <div className="field"><label>Tolerância (min)</label><input type="number" min="0" value={form.toleranciaInicioMin} onChange={e=>setForm(f=>({...f,toleranciaInicioMin:parseInt(e.target.value)||0}))}/></div>
            </div>
            {err && <p style={{color:'var(--err)',fontSize:12,marginBottom:12}}>{err}</p>}
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Salvando...':'Salvar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   ATRIBUIÇÕES
   ══════════════════════════════════════════════════════════════ */
export function RondaAtribuicoesScreen({ user }) {
  const hoje = new Date().toISOString().slice(0,10);
  const [data, setData] = useState(hoje);
  const [atribuicoes, setAtribuicoes] = useState([]);
  const [programacoes, setProgramacoes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ programacaoId:'', usuarioId:'', dataTurno:hoje });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const canEdit = hasPerm(user, 'ronda', 'edit');

  const refresh = async (d) => {
    setCarregando(true); setErroCarga('');
    try {
      const [a, p, u] = await Promise.all([API.ronda.atribuicoes(d), API.ronda.programacoes(), API.listarUsuarios()]);
      setAtribuicoes(a.atribuicoes || []); setProgramacoes(p.programacoes || []); setUsuarios(u.usuarios || u || []);
    } catch (e) { setErroCarga(e.message || 'Não foi possível carregar as atribuições.'); }
    setCarregando(false);
  };
  useEffect(() => { refresh(data); }, [data]);

  const openNew = () => { setForm({ programacaoId:programacoes[0]?.id||'', usuarioId:'', dataTurno:data }); setErr(''); setModal(true); };

  const save = async () => {
    if (!form.programacaoId) { setErr('Selecione a programação.'); return; }
    if (!form.usuarioId) { setErr('Selecione o rondante.'); return; }
    setBusy(true); setErr('');
    try {
      await API.ronda.criarAtribuicao(form);
      await refresh(data); setModal(false);
    } catch (e) { setErr(e.message || 'Não foi possível criar a atribuição.'); }
    setBusy(false);
  };

  const cancelar = async (id) => {
    if (!confirm('Cancelar esta atribuição?')) return;
    try { await API.ronda.cancelarAtribuicao(id); await refresh(data); }
    catch (e) { alert(e.message || 'Não foi possível cancelar.'); }
  };

  if (!hasPerm(user, 'ronda', 'view')) return <Blocked/>;

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title"><h2>Atribuições de Ronda</h2><p>Quem vai rondar em cada turno</p></div>
        {canEdit && <button className="btn btn-primary" onClick={openNew}><Icon name="plus" size={14}/> Nova Atribuição</button>}
      </div>

      <div className="card" style={{marginBottom:16,display:'flex',alignItems:'center',gap:10}}>
        <label style={{fontSize:13,color:'var(--ink-3)'}}>Data:</label>
        <input type="date" value={data} onChange={e=>setData(e.target.value)}/>
      </div>

      {carregando && <div className="card" style={{padding:20,color:'var(--ink-3)',fontSize:13,marginBottom:16}}>Carregando…</div>}
      {erroCarga && (
        <div className="card" style={{padding:16,marginBottom:16,background:'var(--err-bg)',border:'1px solid #f5c2c2'}}>
          <div style={{color:'var(--err)',fontSize:13,fontWeight:600,marginBottom:6}}>{erroCarga}</div>
          <button className="btn btn-ghost" onClick={()=>refresh(data)}>Tentar de novo</button>
        </div>
      )}

      {!carregando && !erroCarga && (atribuicoes.length===0 ? (
        <div className="card" style={{textAlign:'center',padding:40,color:'var(--ink-3)'}}>
          <Icon name="users" size={32} style={{margin:'0 auto 12px',display:'block'}}/>
          <p>Nenhuma atribuição para esta data.</p>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {atribuicoes.map(a => (
            <div key={a.id} className="card" style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,flexWrap:'wrap'}}>
              <div>
                <div style={{fontWeight:600,fontSize:14}}>{a.usuarioNome}</div>
                <div style={{fontSize:12,color:'var(--ink-3)',marginTop:2}}>{a.programacaoNome} · {a.rotaNome} · {a.horaInicio}</div>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <span className={`status-pill ${a.status==='cancelada'?'muted':'ok'}`}><span className="ind"/>{a.status==='cancelada'?'Cancelada':'Atribuída'}</span>
                {canEdit && a.status!=='cancelada' && <button className="btn btn-danger btn-sm" onClick={()=>cancelar(a.id)}><Icon name="x" size={12}/></button>}
              </div>
            </div>
          ))}
        </div>
      ))}

      {modal && (
        <div className="modal-overlay" onClick={()=>setModal(false)}>
          <div className="modal-card" onClick={e=>e.stopPropagation()} style={{maxWidth:440}}>
            <button className="close-x" onClick={()=>setModal(false)}><Icon name="x" size={16}/></button>
            <h3>Nova Atribuição</h3>
            <div className="field"><label>Programação <span className="req">*</span></label>
              <select value={form.programacaoId} onChange={e=>setForm(f=>({...f,programacaoId:e.target.value}))}>
                <option value="">Selecione...</option>
                {programacoes.map(p=><option key={p.id} value={p.id}>{p.nome} — {p.rotaNome} ({p.horaInicio})</option>)}
              </select>
            </div>
            <div className="field"><label>Rondante <span className="req">*</span></label>
              <select value={form.usuarioId} onChange={e=>setForm(f=>({...f,usuarioId:e.target.value}))}>
                <option value="">Selecione...</option>
                {usuarios.map(u=><option key={u.id} value={u.id}>{u.nome}</option>)}
              </select>
            </div>
            <div className="field"><label>Data do Turno <span className="req">*</span></label>
              <input type="date" value={form.dataTurno} onChange={e=>setForm(f=>({...f,dataTurno:e.target.value}))}/>
            </div>
            {err && <p style={{color:'var(--err)',fontSize:12,marginBottom:12}}>{err}</p>}
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button className="btn btn-ghost" onClick={()=>setModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Salvando...':'Atribuir'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   PAINEL DO DIA
   ══════════════════════════════════════════════════════════════ */
/* Fase Ronda-4, Etapa 1 (ver DOC/ADR/0016): atraso é calculado no cliente, a
   partir de dados que o worker já devolve (nenhuma rota nova) — mesma
   filosofia "sem infra nova" da Fase Ronda-3 (sem Cron Trigger dedicado,
   sem Web Push). Só faz sentido pra HOJE: olhar atraso de um dia passado
   não tem utilidade (a ronda ou aconteceu ou não, já é histórico). */
function calcularAtrasos(atribuicoes, execucoes, data, hoje) {
  if (data !== hoje) return [];
  const agora = Date.now();
  return (atribuicoes || []).filter(a => {
    if (a.status !== 'atribuida' || !a.horaInicio) return false;
    const jaIniciou = (execucoes || []).some(ex => ex.atribuicaoId === a.id);
    if (jaIniciou) return false;
    const [h, m] = a.horaInicio.split(':').map(Number);
    const limite = new Date();
    limite.setHours(h, m + (a.toleranciaInicioMin || 0), 0, 0);
    return agora > limite.getTime();
  });
}

export function RondaPainelScreen({ user }) {
  const hoje = new Date().toISOString().slice(0,10);
  const [data, setData] = useState(hoje);
  const [execucoes, setExecucoes] = useState([]);
  const [pontos, setPontos] = useState([]);
  const [atribuicoes, setAtribuicoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const [detalhe, setDetalhe] = useState(null);
  const [lightbox, setLightbox] = useState(null); // checkpointId
  const [trajeto, setTrajeto] = useState(null); // { execucaoId, pontos: [{lat,lng,criadoEm}] } | null
  const [carregandoTrajeto, setCarregandoTrajeto] = useState(false);

  const refresh = async (d, { silencioso } = {}) => {
    if (!silencioso) { setCarregando(true); setErroCarga(''); }
    try {
      const [ex, pt, at] = await Promise.all([API.ronda.execucoes(d), API.ronda.pontos(), API.ronda.atribuicoes(d)]);
      setExecucoes(ex.execucoes || []); setPontos(pt.pontos || []); setAtribuicoes(at.atribuicoes || []);
    } catch (e) { if (!silencioso) setErroCarga(e.message || 'Não foi possível carregar o painel.'); }
    if (!silencioso) setCarregando(false);
  };
  useEffect(() => { refresh(data); }, [data]);

  /* Fase Ronda-3: painel se atualiza sozinho enquanto a aba estiver aberta —
     decisão do dono foi não trazer Web Push (infra nova) pra sinalizar
     pânico/ocorrência grave; um refresh de ~15s cobre isso pra quem estiver
     de olho no painel, sem infra a mais. "Silencioso": não pisca o spinner
     de carregamento a cada rodada, só troca os dados quando chegam. */
  useEffect(() => {
    const id = setInterval(() => refresh(data, { silencioso: true }), 15000);
    return () => clearInterval(id);
  }, [data]);

  const abrirDetalhe = async (id) => {
    try { const r = await API.ronda.execucao(id); setDetalhe(r); }
    catch (e) { alert(e.message || 'Não foi possível carregar o detalhe.'); }
  };

  const abrirTrajeto = async (execucaoId) => {
    setCarregandoTrajeto(true);
    try { const r = await API.ronda.trajeto(execucaoId); setTrajeto({ execucaoId, pontos: r.trajeto || [] }); }
    catch (e) { alert(e.message || 'Não foi possível carregar o trajeto.'); }
    setCarregandoTrajeto(false);
  };

  if (!hasPerm(user, 'ronda', 'view')) return <Blocked/>;

  const execucoesAoVivo = execucoes.filter(ex => ex.status === 'em_andamento');
  const atrasos = calcularAtrasos(atribuicoes, execucoes, data, hoje);
  const panicos = execucoes.filter(ex => ex.panicoAcionado);
  const comDesvioAndamento = execucoes.filter(ex => ex.status === 'em_andamento' && ex.qtdDesvios > 0);
  const ocorrenciasAbertas = execucoes.reduce((soma, ex) => soma + (ex.qtdOcorrenciasAbertas || 0), 0);
  const temAlerta = atrasos.length > 0 || panicos.length > 0 || comDesvioAndamento.length > 0 || ocorrenciasAbertas > 0;

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title"><h2>Painel de Ronda</h2><p>Execuções do dia — atualiza sozinho a cada 15s</p></div>
      </div>

      <div className="card" style={{marginBottom:16,display:'flex',alignItems:'center',gap:10}}>
        <label style={{fontSize:13,color:'var(--ink-3)'}}>Data:</label>
        <input type="date" value={data} onChange={e=>setData(e.target.value)}/>
      </div>

      <div className="card" style={{marginBottom:16,padding:16, ...(temAlerta?{border:'1px solid var(--err)'}:{})}}>
        <div style={{fontWeight:600,fontSize:14,marginBottom:temAlerta?10:0,display:'flex',alignItems:'center',gap:8}}>
          <Icon name="alert" size={15}/> Alertas do dia
        </div>
        {!temAlerta ? (
          <p style={{fontSize:12,color:'var(--ink-3)',margin:0}}>Nenhum alerta no momento.</p>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {panicos.length > 0 && (
              <div style={{fontSize:13,color:'var(--err)',fontWeight:600}}>🚨 {panicos.length} ronda(s) com pânico acionado</div>
            )}
            {atrasos.map(a => (
              <div key={a.id} style={{fontSize:12,color:'var(--warn)'}}>
                ⏰ {a.usuarioNome} — {a.programacaoNome} previsto para {a.horaInicio} e ainda não iniciou (tolerância de {a.toleranciaInicioMin} min vencida)
              </div>
            ))}
            {comDesvioAndamento.length > 0 && (
              <div style={{fontSize:12,color:'var(--warn)'}}>⚠️ {comDesvioAndamento.length} ronda(s) em andamento com checkpoint fora de raio/inválido</div>
            )}
            {ocorrenciasAbertas > 0 && (
              <div style={{fontSize:12,color:'var(--err)'}}>📋 {ocorrenciasAbertas} ocorrência(s) aberta(s) hoje</div>
            )}
          </div>
        )}
      </div>

      <div className="card" style={{marginBottom:16,padding:16}}>
        <div style={{fontWeight:600,fontSize:14,marginBottom:10,display:'flex',alignItems:'center',gap:8}}>
          <Icon name="map-pin" size={15}/> Mapa em tempo real
        </div>
        <RondaMapa pontos={pontos} execucoesAoVivo={execucoesAoVivo}/>
        <p style={{fontSize:11,color:'var(--ink-4)',marginTop:8}}>Posição atualizada pelo aparelho do rondante a cada ~30s enquanto a ronda está em andamento.</p>
      </div>

      {carregando && <div className="card" style={{padding:20,color:'var(--ink-3)',fontSize:13,marginBottom:16}}>Carregando…</div>}
      {erroCarga && (
        <div className="card" style={{padding:16,marginBottom:16,background:'var(--err-bg)',border:'1px solid #f5c2c2'}}>
          <div style={{color:'var(--err)',fontSize:13,fontWeight:600,marginBottom:6}}>{erroCarga}</div>
          <button className="btn btn-ghost" onClick={()=>refresh(data)}>Tentar de novo</button>
        </div>
      )}

      {!carregando && !erroCarga && (execucoes.length===0 ? (
        <div className="card" style={{textAlign:'center',padding:40,color:'var(--ink-3)'}}>
          <Icon name="map-pin" size={32} style={{margin:'0 auto 12px',display:'block'}}/>
          <p>Nenhuma ronda executada nesta data.</p>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {execucoes.map(ex => {
            const s = statusExecucaoInfo(ex.status);
            return (
              <div key={ex.id} className={`card ${ex.panicoAcionado?'ronda-panico-destaque':''}`} style={{cursor:'pointer'}} onClick={()=>abrirDetalhe(ex.id)}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                  <div>
                    <div style={{fontWeight:600,fontSize:14}}>{ex.panicoAcionado && '🚨 '}{ex.rotaNome}</div>
                    <div style={{fontSize:12,color:'var(--ink-3)',marginTop:2}}>{ex.usuarioNome} · início {fmtDate(ex.iniciadoEm)}{ex.finalizadoEm?` · fim ${fmtDate(ex.finalizadoEm)}`:''}</div>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    {ex.totalPontosRota!=null && <span style={{fontSize:12,color:'var(--ink-3)'}}>{ex.totalCheckpoints||0}/{ex.totalPontosRota} pontos</span>}
                    {ex.qtdDesvios>0 && <span style={{fontSize:11,color:'var(--warn)'}}>{ex.qtdDesvios} desvio(s)</span>}
                    {ex.qtdOcorrenciasAbertas>0 && <span style={{fontSize:11,color:'var(--err)'}}>{ex.qtdOcorrenciasAbertas} ocorrência(s) aberta(s)</span>}
                    <span className={`status-pill ${s.cls}`}><span className="ind"/>{s.label}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {detalhe && (
        <div className="modal-overlay" onClick={()=>setDetalhe(null)}>
          <div className="modal-card" onClick={e=>e.stopPropagation()} style={{maxWidth:520}}>
            <button className="close-x" onClick={()=>setDetalhe(null)}><Icon name="x" size={16}/></button>
            <h3>{detalhe.execucao.rotaNome}</h3>
            <p style={{fontSize:13,color:'var(--ink-3)',marginBottom:8}}>{detalhe.execucao.usuarioNome} · início {fmtDate(detalhe.execucao.iniciadoEm)}</p>
            <button className="btn btn-ghost btn-sm" style={{marginBottom:16}} onClick={()=>abrirTrajeto(detalhe.execucao.id)} disabled={carregandoTrajeto}>
              <Icon name="map-pin" size={13}/> {carregandoTrajeto?'Carregando...':'Ver trajeto percorrido'}
            </button>
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {(detalhe.checkpoints||[]).map(c => {
                const s = statusCheckpointInfo(c.status);
                return (
                  <div key={c.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:10,border:'1px solid var(--line)',borderRadius:8,padding:'8px 12px'}}>
                    {c.temFoto && (
                      <RondaFoto checkpointId={c.id} onClick={()=>setLightbox(c.id)}
                        style={{width:44,height:44,objectFit:'cover',borderRadius:6,cursor:'pointer',flexShrink:0}}/>
                    )}
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:600}}>{c.sequenciaEsperada}. {c.pontoNome}</div>
                      <div style={{fontSize:11,color:'var(--ink-4)'}}>{fmtDate(c.checadoEm)}{c.distanciaM!=null?` · ${c.distanciaM}m`:''}</div>
                    </div>
                    <span className={`status-pill ${s.cls}`}><span className="ind"/>{s.label}</span>
                  </div>
                );
              })}
              {(!detalhe.checkpoints || detalhe.checkpoints.length===0) && <p style={{fontSize:12,color:'var(--ink-3)'}}>Nenhum checkpoint registrado ainda.</p>}
            </div>
          </div>
        </div>
      )}

      {lightbox && (
        <div className="modal-overlay" onClick={()=>setLightbox(null)}>
          <RondaFoto checkpointId={lightbox} alt="Foto do checkpoint"
            style={{maxWidth:'90vw',maxHeight:'90vh',borderRadius:12,boxShadow:'0 24px 48px rgba(0,0,0,0.4)'}}/>
        </div>
      )}

      {trajeto && (
        <div className="modal-overlay" onClick={()=>setTrajeto(null)}>
          <div className="modal-card" onClick={e=>e.stopPropagation()} style={{maxWidth:640}}>
            <button className="close-x" onClick={()=>setTrajeto(null)}><Icon name="x" size={16}/></button>
            <h3>🛤️ Trajeto percorrido</h3>
            <p style={{fontSize:12,color:'var(--ink-3)',marginBottom:12}}>Início em verde, última posição registrada em vermelho.</p>
            <RondaTrajetoMapa pontos={pontos} trajeto={trajeto.pontos}/>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MINHAS RONDAS — visão do rondante (lista → execução)
   ══════════════════════════════════════════════════════════════ */
export function RondaMinhasRondasScreen({ user }) {
  const hoje = new Date().toISOString().slice(0,10);
  const [rondas, setRondas] = useState([]);
  const [rotas, setRotas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const [iniciando, setIniciando] = useState(null);

  const [execucao, setExecucao] = useState(null);   // { id, rotaId, ... }
  const [rotaAtual, setRotaAtual] = useState(null);  // rota completa (com pontos)
  const [checkpoints, setCheckpoints] = useState([]); // resultados já registrados nesta execução
  const [lendoQR, setLendoQR] = useState(false);
  const [lendoNfc, setLendoNfc] = useState(false);
  const [codigoManual, setCodigoManual] = useState('');
  const [registrando, setRegistrando] = useState(false);
  const [ultimoResultado, setUltimoResultado] = useState(null);
  const [finalizando, setFinalizando] = useState(false);
  const [resumoFinal, setResumoFinal] = useState(null);
  const [erroExec, setErroExec] = useState('');
  const [enviandoFoto, setEnviandoFoto] = useState(false);
  const fotoInputRef = useRef(null);
  const [acionandoPanico, setAcionandoPanico] = useState(false);
  const [modalOcorrencia, setModalOcorrencia] = useState(false);
  const [formOcorrencia, setFormOcorrencia] = useState({ tipo:'outro', descricao:'', gravidade:'baixa' });
  const [enviandoOcorrencia, setEnviandoOcorrencia] = useState(false);
  const [erroOcorrencia, setErroOcorrencia] = useState('');
  const [ocorrenciaEnviada, setOcorrenciaEnviada] = useState(false);

  const refresh = async () => {
    setCarregando(true); setErroCarga('');
    try {
      const [r, rt] = await Promise.all([API.ronda.minhasRondas(hoje), API.ronda.rotas()]);
      setRondas(r.rondas || []); setRotas(rt.rotas || []);
    } catch (e) { setErroCarga(e.message || 'Não foi possível carregar suas rondas.'); }
    setCarregando(false);
  };
  useEffect(() => { refresh(); }, []);

  if (!hasPerm(user, 'ronda', 'view')) return <Blocked/>;

  const iniciar = async (ronda) => {
    setIniciando(ronda.atribuicaoId); setErroExec('');
    try {
      const { lat, lng } = await obterPosicao();
      const r = await API.ronda.iniciarExecucao({
        rotaId: ronda.rotaId, atribuicaoId: ronda.atribuicaoId, lat, lng,
        deviceId: obterDeviceId(), nomeDispositivo: nomeDispositivoAtual(),
      });
      const rota = rotas.find(x => x.id === ronda.rotaId) || null;
      setExecucao(r.execucao); setRotaAtual(rota); setCheckpoints([]); setUltimoResultado(null); setResumoFinal(null);
    } catch (e) { setErroExec(e.message || 'Não foi possível iniciar a ronda.'); }
    setIniciando(null);
  };

  /* Fase Ronda-3: manda a posição atual pro mapa em tempo real do Painel a
     cada ~30s enquanto a ronda está em andamento. Nunca trava o fluxo se
     falhar (rede ruim, GPS indisponível) — é só "melhor esforço", igual à
     foto do checkpoint; a execução em si não depende disso. Para assim que
     a ronda é finalizada (execucao continua setado até voltarParaLista, mas
     resumoFinal já existe — checa os dois). */
  useEffect(() => {
    if (!execucao || resumoFinal) return;
    const enviar = async () => {
      try {
        const { lat, lng } = await obterPosicao();
        if (lat != null && lng != null) await API.ronda.atualizarPosicao(execucao.id, { lat, lng });
      } catch { /* melhor esforço — próxima rodada tenta de novo */ }
    };
    enviar();
    const id = setInterval(enviar, 30000);
    return () => clearInterval(id);
  }, [execucao?.id, !!resumoFinal]);

  /* Botão de pânico — confirmação explícita antes de acionar (não é um
     toque acidental que se resolve sozinho: dispara ocorrência gravidade
     alta e destaca a execução em vermelho no Painel). */
  const acionarPanico = async () => {
    if (!execucao) return;
    if (!confirm('Acionar o botão de pânico? Isso avisa o supervisor imediatamente.')) return;
    setAcionandoPanico(true); setErroExec('');
    try {
      const { lat, lng } = await obterPosicao();
      await API.ronda.acionarPanico(execucao.id, { lat, lng });
    } catch (e) { setErroExec(e.message || 'Não foi possível acionar o pânico.'); }
    setAcionandoPanico(false);
  };

  const abrirOcorrencia = () => {
    setFormOcorrencia({ tipo:'outro', descricao:'', gravidade:'baixa' });
    setErroOcorrencia(''); setOcorrenciaEnviada(false); setModalOcorrencia(true);
  };
  const enviarOcorrencia = async () => {
    if (!formOcorrencia.descricao.trim()) { setErroOcorrencia('Descreva a ocorrência.'); return; }
    setEnviandoOcorrencia(true); setErroOcorrencia('');
    try {
      /* Ponto opcional — usa o último checkpoint já registrado (onde o
         rondante está agora), não o próximo (ainda não visitado). */
      await API.ronda.criarOcorrencia(execucao.id, {
        tipo: formOcorrencia.tipo, descricao: formOcorrencia.descricao.trim(), gravidade: formOcorrencia.gravidade,
        pontoId: ultimoResultado?.pontoId || null,
      });
      setOcorrenciaEnviada(true);
    } catch (e) { setErroOcorrencia(e.message || 'Não foi possível registrar a ocorrência.'); }
    setEnviandoOcorrencia(false);
  };

  const proximoPonto = () => {
    if (!rotaAtual) return null;
    return rotaAtual.pontos[checkpoints.length] || null;
  };

  const registrarCheckpoint = async (codigoLido, nfcUid) => {
    const ponto = proximoPonto();
    if (!ponto) return;
    setRegistrando(true); setErroExec('');
    try {
      const { lat, lng } = await obterPosicao();
      const r = await API.ronda.checkpoint(execucao.id, { pontoId: ponto.pontoId, codigoLido, nfcUid, lat, lng });
      setCheckpoints(l => [...l, r.checkpoint]);
      setUltimoResultado(r.checkpoint);
    } catch (e) { setErroExec(e.message || 'Não foi possível registrar o checkpoint.'); }
    setRegistrando(false);
  };

  const onQRLido = (codigoLido) => { setLendoQR(false); registrarCheckpoint(codigoLido); };
  const registrarManual = () => {
    if (!codigoManual.trim()) return;
    registrarCheckpoint(codigoManual.trim());
    setCodigoManual('');
  };

  /* Fase Ronda-4, Etapa 5: leitura por tag NFC como alternativa ao QR —
     sempre "melhor esforço", nunca é a única forma de confirmar o ponto. */
  const lerNfc = async () => {
    setLendoNfc(true); setErroExec('');
    try { const uid = await lerTagNFC(); registrarCheckpoint(null, uid); }
    catch (e) { setErroExec(e.message || 'Não foi possível ler a tag NFC.'); }
    setLendoNfc(false);
  };

  /* Foto de evidência do checkpoint (Fase Ronda-2, ver DOC/ADR/0014) —
     OPCIONAL e um segundo passo, só sobre o checkpoint que acabou de ser
     registrado. O checkpoint já está gravado mesmo se o envio da foto
     falhar aqui — mesmo raciocínio de anexarFoto em Segurança. */
  const anexarFoto = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || !ultimoResultado) return;
    setEnviandoFoto(true); setErroExec('');
    API.ronda.anexarFotoCheckpoint(ultimoResultado.id, file)
      .then(r => {
        setUltimoResultado(u => (u && u.id === r.checkpoint.id) ? r.checkpoint : u);
        setCheckpoints(l => l.map(c => c.id === r.checkpoint.id ? r.checkpoint : c));
      })
      .catch(err => setErroExec('Checkpoint registrado, mas a foto não foi enviada: ' + (err.message || 'erro desconhecido')))
      .finally(() => setEnviandoFoto(false));
  };

  const finalizar = async () => {
    setFinalizando(true); setErroExec('');
    try {
      const { lat, lng } = await obterPosicao();
      const r = await API.ronda.finalizarExecucao(execucao.id, { lat, lng });
      setResumoFinal(r.execucao);
    } catch (e) { setErroExec(e.message || 'Não foi possível finalizar a ronda.'); }
    setFinalizando(false);
  };

  const voltarParaLista = () => {
    setExecucao(null); setRotaAtual(null); setCheckpoints([]); setUltimoResultado(null); setResumoFinal(null);
    refresh();
  };

  /* ── Tela de execução ── */
  if (execucao) {
    if (resumoFinal) {
      const s = statusExecucaoInfo(resumoFinal.status);
      return (
        <div className="main-pad">
          <div className="card" style={{textAlign:'center',padding:40,maxWidth:420,margin:'0 auto'}}>
            <Icon name="check-circle" size={40} style={{margin:'0 auto 16px',display:'block',color:'var(--ok)'}}/>
            <h3>Ronda finalizada</h3>
            <p style={{fontSize:13,color:'var(--ink-3)',margin:'8px 0 16px'}}>{rotaAtual?.nome}</p>
            <span className={`status-pill ${s.cls}`} style={{marginBottom:20}}><span className="ind"/>{s.label}</span>
            {resumoFinal.qtdDesvios>0 && <p style={{fontSize:12,color:'var(--warn)',marginTop:10}}>{resumoFinal.qtdDesvios} ponto(s) com desvio.</p>}
            <button className="btn btn-primary" style={{marginTop:20}} onClick={voltarParaLista}>Voltar para Minhas Rondas</button>
          </div>
        </div>
      );
    }

    const ponto = proximoPonto();
    const total = rotaAtual?.pontos.length || 0;
    const feitos = checkpoints.length;
    const completo = ponto === null;

    return (
      <div className="main-pad">
        <div className="page-title-row">
          <div className="page-title"><h2>{rotaAtual?.nome}</h2><p>Ponto {Math.min(feitos+1,total)} de {total}</p></div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <button className="btn btn-ghost btn-sm" onClick={abrirOcorrencia}><Icon name="alert" size={13}/> Registrar Ocorrência</button>
            <button className="ronda-panic-btn" onClick={acionarPanico} disabled={acionandoPanico}>
              <Icon name="alert" size={14}/> {acionandoPanico?'Acionando...':'Pânico'}
            </button>
          </div>
        </div>

        {erroExec && <div className="card" style={{padding:14,marginBottom:16,background:'var(--err-bg)',border:'1px solid #f5c2c2',color:'var(--err)',fontSize:13}}>{erroExec}</div>}

        {ultimoResultado && (
          <div className="card" style={{marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center',gap:10,flexWrap:'wrap'}}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              {ultimoResultado.temFoto && <RondaFoto checkpointId={ultimoResultado.id} style={{width:36,height:36,objectFit:'cover',borderRadius:6}}/>}
              <span style={{fontSize:13}}>Último: {ultimoResultado.pontoNome}</span>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              {!ultimoResultado.temFoto && (
                <button className="btn btn-ghost btn-sm" onClick={()=>fotoInputRef.current?.click()} disabled={enviandoFoto}>
                  <Icon name="camera" size={13}/> {enviandoFoto?'Enviando...':'Tirar Foto'}
                </button>
              )}
              <span className={`status-pill ${statusCheckpointInfo(ultimoResultado.status).cls}`}><span className="ind"/>{statusCheckpointInfo(ultimoResultado.status).label}</span>
            </div>
          </div>
        )}
        <input ref={fotoInputRef} type="file" accept="image/png,image/jpeg" capture="environment" style={{display:'none'}} onChange={anexarFoto}/>

        {!completo ? (
          <div className="card" style={{textAlign:'center',padding:32}}>
            <div style={{fontWeight:600,fontSize:17,marginBottom:4}}>{ponto.pontoNome}</div>
            {ponto.instrucoes && <p style={{fontSize:13,color:'var(--ink-3)',marginBottom:20}}>{ponto.instrucoes}</p>}
            <div style={{display:'flex',gap:8,justifyContent:'center',flexWrap:'wrap'}}>
              <button className="btn btn-primary" onClick={()=>setLendoQR(true)} disabled={registrando}>
                <Icon name="camera" size={15}/> {registrando?'Registrando...':'Ler QR do Ponto'}
              </button>
              {nfcSuportado() && (
                <button className="btn btn-ghost" onClick={lerNfc} disabled={registrando || lendoNfc}>
                  <Icon name="nfc" size={15}/> {lendoNfc?'Aproxime a tag...':'Ler Tag NFC'}
                </button>
              )}
            </div>
            <div style={{marginTop:20,paddingTop:20,borderTop:'1px solid var(--line)'}}>
              <p style={{fontSize:11,color:'var(--ink-4)',marginBottom:8}}>Câmera indisponível? Digite o código do ponto:</p>
              <div style={{display:'flex',gap:8,justifyContent:'center'}}>
                <input style={{maxWidth:180}} value={codigoManual} onChange={e=>setCodigoManual(e.target.value)} placeholder="Código do ponto"/>
                <button className="btn btn-ghost btn-sm" onClick={registrarManual} disabled={registrando || !codigoManual.trim()}>Registrar</button>
              </div>
            </div>
          </div>
        ) : (
          <div className="card" style={{textAlign:'center',padding:32}}>
            <p style={{fontSize:14,marginBottom:20}}>Todos os pontos desta rota foram checados.</p>
            <button className="btn btn-primary" onClick={finalizar} disabled={finalizando}>{finalizando?'Finalizando...':'Finalizar Ronda'}</button>
          </div>
        )}

        {checkpoints.length>0 && (
          <div style={{marginTop:20,display:'flex',flexDirection:'column',gap:6}}>
            {checkpoints.map((c,i)=>{
              const s = statusCheckpointInfo(c.status);
              return (
                <div key={c.id||i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:12,padding:'6px 10px',border:'1px solid var(--line)',borderRadius:6}}>
                  <span>{i+1}. {c.pontoNome}</span>
                  <span className={`status-pill ${s.cls}`} style={{padding:'2px 7px'}}><span className="ind"/>{s.label}</span>
                </div>
              );
            })}
          </div>
        )}

        {lendoQR && <LeitorQR onLido={onQRLido} onCancelar={()=>setLendoQR(false)}/>}

        {modalOcorrencia && (
          <div className="modal-overlay" onClick={()=>setModalOcorrencia(false)}>
            <div className="modal-card" onClick={e=>e.stopPropagation()} style={{maxWidth:440}}>
              <button className="close-x" onClick={()=>setModalOcorrencia(false)}><Icon name="x" size={16}/></button>
              <h3>Registrar Ocorrência</h3>
              {ocorrenciaEnviada ? (
                <>
                  <p style={{fontSize:13,color:'var(--ok)',margin:'12px 0 20px'}}><Icon name="check-circle" size={14}/> Ocorrência registrada.</p>
                  <div style={{display:'flex',justifyContent:'flex-end'}}>
                    <button className="btn btn-primary" onClick={()=>setModalOcorrencia(false)}>Fechar</button>
                  </div>
                </>
              ) : (
                <>
                  <p style={{fontSize:13,color:'var(--ink-3)',marginBottom:16}}>Relate um problema encontrado durante a ronda — não precisa estar ligado ao ponto atual.</p>
                  <div className="field"><label>Tipo</label>
                    <select value={formOcorrencia.tipo} onChange={e=>setFormOcorrencia(f=>({...f,tipo:e.target.value}))}>
                      {TIPOS_OCORRENCIA.map(([v,l])=><option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div className="field"><label>Gravidade</label>
                    <select value={formOcorrencia.gravidade} onChange={e=>setFormOcorrencia(f=>({...f,gravidade:e.target.value}))}>
                      {GRAVIDADES.map(([v,l])=><option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div className="field"><label>Descrição <span className="req">*</span></label>
                    <textarea rows={3} value={formOcorrencia.descricao} onChange={e=>setFormOcorrencia(f=>({...f,descricao:e.target.value}))} placeholder="O que foi encontrado?"/>
                  </div>
                  {erroOcorrencia && <p style={{color:'var(--err)',fontSize:12,marginBottom:12}}>{erroOcorrencia}</p>}
                  <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
                    <button className="btn btn-ghost" onClick={()=>setModalOcorrencia(false)}>Cancelar</button>
                    <button className="btn btn-primary" onClick={enviarOcorrencia} disabled={enviandoOcorrencia}>{enviandoOcorrencia?'Enviando...':'Registrar'}</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ── Lista de rondas do dia ── */
  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title"><h2>Minhas Rondas</h2><p>Rondas atribuídas a você hoje</p></div>
      </div>

      {carregando && <div className="card" style={{padding:20,color:'var(--ink-3)',fontSize:13,marginBottom:16}}>Carregando…</div>}
      {erroCarga && (
        <div className="card" style={{padding:16,marginBottom:16,background:'var(--err-bg)',border:'1px solid #f5c2c2'}}>
          <div style={{color:'var(--err)',fontSize:13,fontWeight:600,marginBottom:6}}>{erroCarga}</div>
          <button className="btn btn-ghost" onClick={refresh}>Tentar de novo</button>
        </div>
      )}
      {erroExec && !carregando && <div className="card" style={{padding:14,marginBottom:16,background:'var(--err-bg)',border:'1px solid #f5c2c2',color:'var(--err)',fontSize:13}}>{erroExec}</div>}

      {!carregando && !erroCarga && (rondas.length===0 ? (
        <div className="card" style={{textAlign:'center',padding:40,color:'var(--ink-3)'}}>
          <Icon name="map-pin" size={32} style={{margin:'0 auto 12px',display:'block'}}/>
          <p>Nenhuma ronda atribuída a você hoje.</p>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {rondas.map(r => (
            <div key={r.atribuicaoId} className="card" style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,flexWrap:'wrap'}}>
              <div>
                <div style={{fontWeight:600,fontSize:14}}>{r.rotaNome}</div>
                <div style={{fontSize:12,color:'var(--ink-3)',marginTop:2}}>{r.programacaoNome} · início previsto {r.horaInicio}</div>
              </div>
              <button className="btn btn-primary" onClick={()=>iniciar(r)} disabled={iniciando===r.atribuicaoId}>
                {iniciando===r.atribuicaoId ? 'Iniciando...' : 'Iniciar'}
              </button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   OCORRÊNCIAS — Fase Ronda-3 (ver DOC/ADR/0015)
   ══════════════════════════════════════════════════════════════ */
export function RondaOcorrenciasScreen({ user }) {
  const hoje = new Date().toISOString().slice(0,10);
  const [data, setData] = useState(hoje);
  const [status, setStatus] = useState('');
  const [ocorrencias, setOcorrencias] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const [resolvendo, setResolvendo] = useState(null);

  const canEdit = hasPerm(user, 'ronda', 'edit');

  const refresh = async () => {
    setCarregando(true); setErroCarga('');
    try { const r = await API.ronda.ocorrencias(data, status); setOcorrencias(r.ocorrencias || []); }
    catch (e) { setErroCarga(e.message || 'Não foi possível carregar as ocorrências.'); }
    setCarregando(false);
  };
  useEffect(() => { refresh(); }, [data, status]);

  const resolver = async (id) => {
    setResolvendo(id);
    try { await API.ronda.resolverOcorrencia(id); await refresh(); }
    catch (e) { alert(e.message || 'Não foi possível resolver.'); }
    setResolvendo(null);
  };

  if (!hasPerm(user, 'ronda', 'view')) return <Blocked/>;

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title"><h2>Ocorrências</h2><p>Problemas relatados durante as rondas</p></div>
      </div>

      <div className="card" style={{marginBottom:16,display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <label style={{fontSize:13,color:'var(--ink-3)'}}>Data:</label>
          <input type="date" value={data} onChange={e=>setData(e.target.value)}/>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <label style={{fontSize:13,color:'var(--ink-3)'}}>Status:</label>
          <select value={status} onChange={e=>setStatus(e.target.value)}>
            <option value="">Todas</option>
            <option value="aberta">Abertas</option>
            <option value="resolvida">Resolvidas</option>
          </select>
        </div>
      </div>

      {carregando && <div className="card" style={{padding:20,color:'var(--ink-3)',fontSize:13,marginBottom:16}}>Carregando…</div>}
      {erroCarga && (
        <div className="card" style={{padding:16,marginBottom:16,background:'var(--err-bg)',border:'1px solid #f5c2c2'}}>
          <div style={{color:'var(--err)',fontSize:13,fontWeight:600,marginBottom:6}}>{erroCarga}</div>
          <button className="btn btn-ghost" onClick={refresh}>Tentar de novo</button>
        </div>
      )}

      {!carregando && !erroCarga && (ocorrencias.length===0 ? (
        <div className="card" style={{textAlign:'center',padding:40,color:'var(--ink-3)'}}>
          <Icon name="alert" size={32} style={{margin:'0 auto 12px',display:'block'}}/>
          <p>Nenhuma ocorrência para este filtro.</p>
        </div>
      ) : (
        <div className="card" style={{padding:0,overflowX:'auto'}}>
          <table className="users-table">
            <thead>
              <tr><th>Quando</th><th>Rondante</th><th>Tipo</th><th>Gravidade</th><th>Descrição</th><th>Status</th>{canEdit && <th></th>}</tr>
            </thead>
            <tbody>
              {ocorrencias.map(o => {
                const g = gravidadeInfo(o.gravidade);
                return (
                  <tr key={o.id}>
                    <td style={{whiteSpace:'nowrap'}}>{fmtDate(o.criadoEm)}</td>
                    <td>{o.usuarioNome || '—'}</td>
                    <td>{tipoOcorrenciaLabel(o.tipo)}{o.pontoNome ? ` · ${o.pontoNome}` : ''}</td>
                    <td><span className={`status-pill ${g.cls}`}><span className="ind"/>{g.label}</span></td>
                    <td style={{maxWidth:320}}>{o.descricao}</td>
                    <td><span className={`status-pill ${o.status==='resolvida'?'ok':'warn'}`}><span className="ind"/>{o.status==='resolvida'?'Resolvida':'Aberta'}</span></td>
                    {canEdit && (
                      <td>
                        {o.status==='aberta' && (
                          <button className="btn btn-ghost btn-sm" onClick={()=>resolver(o.id)} disabled={resolvendo===o.id}>
                            {resolvendo===o.id?'...':'Resolver'}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

const ALERTA_TIPO_LABEL = { panico: 'Pânico', ocorrencia: 'Ocorrência grave', desvio: 'Desvio na ronda' };
const ALERTA_STATUS_INFO = {
  open:         { label: 'Aberto',       cls: 'err'  },
  acknowledged: { label: 'Reconhecido',  cls: 'warn' },
  closed:       { label: 'Fechado',      cls: 'ok'   },
};

/* ══════════════════════════════════════════════════════════════
   ALERTAS — reconhecimento formal ("confirmar ciência") dos alertas
   automáticos gerados por pânico/ocorrência grave/desvio (Fase Ronda-4,
   Etapa 4). Distinto de "resolver ocorrência": aqui só se registra que
   o supervisor VIU o alerta — o fechamento (status='closed') continua
   automático, disparado quando a ocorrência de origem é resolvida.
   Ajustado para o Intranet Osasco: sem tipo 'atraso' (fica só no painel,
   calculado no cliente — ver Etapa 1), sem push — supervisor confirma
   ciência ao abrir esta tela.
   ══════════════════════════════════════════════════════════════ */
export function RondaAlertasScreen({ user }) {
  const [status, setStatus] = useState('open');
  const [alertas, setAlertas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const [ackId, setAckId] = useState(null);

  const refresh = async () => {
    setCarregando(true); setErroCarga('');
    try { const r = await API.ronda.alertas(status); setAlertas(r.alertas || []); }
    catch (e) { setErroCarga(e.message || 'Não foi possível carregar os alertas.'); }
    setCarregando(false);
  };
  useEffect(() => { refresh(); }, [status]);

  const reconhecer = async (id) => {
    setAckId(id);
    try { await API.ronda.ackAlerta(id); await refresh(); }
    catch (e) { alert(e.message || 'Não foi possível reconhecer o alerta.'); }
    setAckId(null);
  };

  if (!hasPerm(user, 'ronda', 'edit')) return <Blocked/>;

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title"><h2>Alertas</h2><p>Pânico, ocorrências graves e desvios detectados nas rondas</p></div>
      </div>

      <div className="card" style={{marginBottom:16,display:'flex',alignItems:'center',gap:8}}>
        <label style={{fontSize:13,color:'var(--ink-3)'}}>Status:</label>
        <select value={status} onChange={e=>setStatus(e.target.value)}>
          <option value="open">Abertos</option>
          <option value="acknowledged">Reconhecidos</option>
          <option value="closed">Fechados</option>
          <option value="">Todos</option>
        </select>
      </div>

      {carregando && <div className="card" style={{padding:20,color:'var(--ink-3)',fontSize:13,marginBottom:16}}>Carregando…</div>}
      {erroCarga && (
        <div className="card" style={{padding:16,marginBottom:16,background:'var(--err-bg)',border:'1px solid #f5c2c2'}}>
          <div style={{color:'var(--err)',fontSize:13,fontWeight:600,marginBottom:6}}>{erroCarga}</div>
          <button className="btn btn-ghost" onClick={refresh}>Tentar de novo</button>
        </div>
      )}

      {!carregando && !erroCarga && (alertas.length===0 ? (
        <div className="card" style={{textAlign:'center',padding:40,color:'var(--ink-3)'}}>
          <Icon name="alert" size={32} style={{margin:'0 auto 12px',display:'block'}}/>
          <p>Nenhum alerta para este filtro.</p>
        </div>
      ) : (
        <div className="card" style={{padding:0,overflowX:'auto'}}>
          <table className="users-table">
            <thead>
              <tr><th>Quando</th><th>Tipo</th><th>Rota</th><th>Rondante</th><th>Descrição</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {alertas.map(a => {
                const s = ALERTA_STATUS_INFO[a.status] || { label: a.status, cls: '' };
                return (
                  <tr key={a.id}>
                    <td style={{whiteSpace:'nowrap'}}>{fmtDate(a.criadoEm)}</td>
                    <td>{ALERTA_TIPO_LABEL[a.tipo] || a.tipo}</td>
                    <td>{a.rotaNome || '—'}</td>
                    <td>{a.usuarioNome || '—'}</td>
                    <td style={{maxWidth:320}}>{a.descricao}</td>
                    <td><span className={`status-pill ${s.cls}`}><span className="ind"/>{s.label}</span></td>
                    <td>
                      {a.status==='open' && (
                        <button className="btn btn-ghost btn-sm" onClick={()=>reconhecer(a.id)} disabled={ackId===a.id}>
                          {ackId===a.id?'...':'Confirmar ciência'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   AUDITORIA — trilha de alteração de cadastro (Fase Ronda-3)
   Reaproveita a tabela `auditoria` da Fundação — nenhuma tabela nova.
   ══════════════════════════════════════════════════════════════ */
export function RondaAuditoriaScreen({ user }) {
  const hoje = new Date().toISOString().slice(0,10);
  const [data, setData] = useState(hoje);
  const [eventos, setEventos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');

  const refresh = async (d) => {
    setCarregando(true); setErroCarga('');
    try { const r = await API.ronda.auditoria(d); setEventos(r.eventos || []); }
    catch (e) { setErroCarga(e.message || 'Não foi possível carregar a auditoria.'); }
    setCarregando(false);
  };
  useEffect(() => { refresh(data); }, [data]);

  if (!hasPerm(user, 'ronda', 'edit')) return <Blocked/>;

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title"><h2>Auditoria — Alterações de Cadastro</h2><p>Quem mudou o quê no cadastro de Ronda</p></div>
        <input type="date" value={data} onChange={e=>setData(e.target.value)}/>
      </div>

      {carregando && <div className="card" style={{padding:20,color:'var(--ink-3)',fontSize:13,marginBottom:16}}>Carregando…</div>}
      {erroCarga && (
        <div className="card" style={{padding:16,marginBottom:16,background:'var(--err-bg)',border:'1px solid #f5c2c2'}}>
          <div style={{color:'var(--err)',fontSize:13,fontWeight:600,marginBottom:6}}>{erroCarga}</div>
          <button className="btn btn-ghost" onClick={()=>refresh(data)}>Tentar de novo</button>
        </div>
      )}

      {!carregando && !erroCarga && (
        <div className="card" style={{padding:0,overflowX:'auto'}}>
          <table className="users-table">
            <thead><tr><th>Quando</th><th>Usuário</th><th>Ação</th><th>Entidade</th><th>Detalhes</th></tr></thead>
            <tbody>
              {eventos.map(e => (
                <tr key={e.id}>
                  <td style={{whiteSpace:'nowrap'}}>{fmtDate(e.criadoEm)}</td>
                  <td>{e.login || '—'}</td>
                  <td>{e.acao}</td>
                  <td>{e.entidade || '—'}</td>
                  <td style={{fontSize:11,color:'var(--ink-3)'}}>{e.detalhe ? JSON.stringify(e.detalhe) : ''}</td>
                </tr>
              ))}
              {eventos.length===0 && <tr><td colSpan={5} style={{textAlign:'center',color:'var(--ink-3)',padding:24}}>Nenhuma alteração nesta data.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   RELATÓRIO DE CONFORMIDADE — Fase Ronda-3
   Uso interno do supervisor: window.print() + @media print (mesmo padrão
   do VicStaffO — nenhum módulo deste projeto traz jsPDF/SheetJS só pra
   isso). Período padrão de última semana, seletor livre pra qualquer
   intervalo.
   ══════════════════════════════════════════════════════════════ */
export function RondaRelatorioScreen({ user }) {
  const hoje = new Date().toISOString().slice(0,10);
  const seiDiasAtras = new Date(Date.now() - 6*86400000).toISOString().slice(0,10);
  const [inicio, setInicio] = useState(seiDiasAtras);
  const [fim, setFim] = useState(hoje);
  const [rel, setRel] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  const gerar = async () => {
    setCarregando(true); setErro('');
    try { setRel(await API.ronda.relatorio(inicio, fim)); }
    catch (e) { setErro(e.message || 'Não foi possível gerar o relatório.'); setRel(null); }
    setCarregando(false);
  };
  useEffect(() => { gerar(); }, []);

  const ultimaSemana = () => { setInicio(seiDiasAtras); setFim(hoje); };
  const ultimoMes = () => { setInicio(new Date(Date.now() - 29*86400000).toISOString().slice(0,10)); setFim(hoje); };

  if (!hasPerm(user, 'ronda', 'edit')) return <Blocked/>;

  return (
    <div className="main-pad">
      <div className="page-title-row no-print">
        <div className="page-title"><h2>Relatório de Conformidade</h2><p>Previstas x realizadas, desvios e ocorrências no período</p></div>
      </div>

      <div className="card no-print" style={{marginBottom:16,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
        <label style={{fontSize:13,color:'var(--ink-3)'}}>De</label>
        <input type="date" value={inicio} onChange={e=>setInicio(e.target.value)}/>
        <label style={{fontSize:13,color:'var(--ink-3)'}}>até</label>
        <input type="date" value={fim} onChange={e=>setFim(e.target.value)}/>
        <button className="btn btn-ghost btn-sm" onClick={ultimaSemana}>Última semana</button>
        <button className="btn btn-ghost btn-sm" onClick={ultimoMes}>Último mês</button>
        <button className="btn btn-primary btn-sm" onClick={gerar} disabled={carregando}>{carregando?'Gerando...':'Gerar'}</button>
        {rel && <button className="btn btn-ghost btn-sm" onClick={()=>window.print()}><Icon name="printer" size={13}/> Imprimir / Exportar PDF</button>}
      </div>

      {erro && <div className="card no-print" style={{padding:16,marginBottom:16,background:'var(--err-bg)',border:'1px solid #f5c2c2',color:'var(--err)',fontSize:13}}>{erro}</div>}

      {rel && (
        <div id="ronda-relatorio-print">
          <div className="card" style={{marginBottom:16}}>
            <h3 style={{marginBottom:2}}>Relatório de Conformidade — Gestão de Ronda</h3>
            <p style={{fontSize:12,color:'var(--ink-3)'}}>Período: {rel.periodo.inicio} a {rel.periodo.fim}</p>
          </div>

          <div className="seg-stat-row">
            <div className="seg-stat-box"><div className="sv">{rel.previstas}</div><div className="sl">Previstas</div></div>
            <div className="seg-stat-box"><div className="sv">{rel.realizadas}</div><div className="sl">Realizadas</div></div>
            <div className="seg-stat-box"><div className="sv">{rel.taxaConformidade!=null?rel.taxaConformidade+'%':'—'}</div><div className="sl">Taxa de conformidade</div></div>
            <div className="seg-stat-box"><div className="sv">{rel.taxaExecucao!=null?rel.taxaExecucao+'%':'—'}</div><div className="sl">Taxa de execução</div></div>
          </div>
          <div className="seg-stat-row">
            <div className="seg-stat-box"><div className="sv">{rel.concluidasSemDesvio}</div><div className="sl">Concluídas sem desvio</div></div>
            <div className="seg-stat-box"><div className="sv">{rel.concluidasComDesvio}</div><div className="sl">Concluídas c/ desvio</div></div>
            <div className="seg-stat-box"><div className="sv">{rel.emAndamento}</div><div className="sl">Em andamento</div></div>
            <div className="seg-stat-box"><div className="sv">{rel.ocorrenciasAbertas}</div><div className="sl">Ocorrências abertas</div></div>
          </div>

          <div className="card" style={{marginBottom:16,padding:0,overflowX:'auto'}}>
            <div style={{padding:'14px 16px',fontWeight:600,fontSize:13,borderBottom:'1px solid var(--line)'}}>Ocorrências por tipo/gravidade</div>
            <table className="users-table">
              <thead><tr><th>Tipo</th><th>Gravidade</th><th>Quantidade</th></tr></thead>
              <tbody>
                {rel.ocorrenciasPorTipo.map((o,i) => (
                  <tr key={i}><td>{tipoOcorrenciaLabel(o.tipo)}</td><td>{gravidadeInfo(o.gravidade).label}</td><td>{o.quantidade}</td></tr>
                ))}
                {rel.ocorrenciasPorTipo.length===0 && <tr><td colSpan={3} style={{textAlign:'center',color:'var(--ink-3)',padding:16}}>Nenhuma ocorrência no período.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="card" style={{padding:0,overflowX:'auto'}}>
            <div style={{padding:'14px 16px',fontWeight:600,fontSize:13,borderBottom:'1px solid var(--line)'}}>Ranking de pontos com mais desvio</div>
            <table className="users-table">
              <thead><tr><th>Ponto</th><th>Desvios</th></tr></thead>
              <tbody>
                {rel.rankingDesvios.map(r => <tr key={r.pontoId}><td>{r.pontoNome}</td><td>{r.quantidade}</td></tr>)}
                {rel.rankingDesvios.length===0 && <tr><td colSpan={2} style={{textAlign:'center',color:'var(--ink-3)',padding:16}}>Nenhum desvio no período.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   DISPOSITIVOS POR RONDANTE — Fase Ronda-3
   ══════════════════════════════════════════════════════════════ */
export function RondaDispositivosScreen({ user }) {
  const [dispositivos, setDispositivos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const [ocupado, setOcupado] = useState(null);

  const refresh = async () => {
    setCarregando(true); setErroCarga('');
    try { const r = await API.ronda.dispositivos(); setDispositivos(r.dispositivos || []); }
    catch (e) { setErroCarga(e.message || 'Não foi possível carregar os dispositivos.'); }
    setCarregando(false);
  };
  useEffect(() => { refresh(); }, []);

  const autorizar = async (id) => {
    setOcupado(id);
    try { await API.ronda.autorizarDispositivo(id); await refresh(); }
    catch (e) { alert(e.message || 'Não foi possível autorizar.'); }
    setOcupado(null);
  };
  const revogar = async (id) => {
    setOcupado(id);
    try { await API.ronda.revogarDispositivo(id); await refresh(); }
    catch (e) { alert(e.message || 'Não foi possível revogar.'); }
    setOcupado(null);
  };

  if (!hasPerm(user, 'ronda', 'edit')) return <Blocked/>;

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title"><h2>Dispositivos por Rondante</h2><p>O primeiro dispositivo que cada rondante usa pra iniciar uma ronda vira autorizado automaticamente. Um dispositivo diferente aparecendo depois gera um aviso aqui — a ronda continua funcionando normalmente, isso é só um sinal de possível conta compartilhada.</p></div>
      </div>

      {carregando && <div className="card" style={{padding:20,color:'var(--ink-3)',fontSize:13,marginBottom:16}}>Carregando…</div>}
      {erroCarga && (
        <div className="card" style={{padding:16,marginBottom:16,background:'var(--err-bg)',border:'1px solid #f5c2c2'}}>
          <div style={{color:'var(--err)',fontSize:13,fontWeight:600,marginBottom:6}}>{erroCarga}</div>
          <button className="btn btn-ghost" onClick={refresh}>Tentar de novo</button>
        </div>
      )}

      {!carregando && !erroCarga && (
        <div className="card" style={{padding:0,overflowX:'auto'}}>
          <table className="users-table">
            <thead><tr><th>Rondante</th><th>Dispositivo</th><th>Primeiro uso</th><th>Último uso</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {dispositivos.map(d => (
                <tr key={d.id}>
                  <td>{d.usuarioNome || '—'}</td>
                  <td>{d.nomeDispositivo || 'Dispositivo'}</td>
                  <td style={{whiteSpace:'nowrap'}}>{fmtDate(d.primeiroUso)}</td>
                  <td style={{whiteSpace:'nowrap'}}>{fmtDate(d.ultimoUso)}</td>
                  <td><span className={`status-pill ${d.autorizado?'ok':'warn'}`}><span className="ind"/>{d.autorizado?'Autorizado':'Não reconhecido'}</span></td>
                  <td>
                    {d.autorizado
                      ? <button className="btn btn-danger btn-sm" onClick={()=>revogar(d.id)} disabled={ocupado===d.id}>{ocupado===d.id?'...':'Revogar'}</button>
                      : <button className="btn btn-ghost btn-sm" onClick={()=>autorizar(d.id)} disabled={ocupado===d.id}>{ocupado===d.id?'...':'Autorizar'}</button>}
                  </td>
                </tr>
              ))}
              {dispositivos.length===0 && <tr><td colSpan={6} style={{textAlign:'center',color:'var(--ink-3)',padding:24}}>Nenhum dispositivo registrado ainda.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
